import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { bottomNavItems, resolveActiveItem, type NavItem } from './navItems';

export interface BottomNavProps {
  /**
   * Optional override for the active tab id. When omitted (the common
   * case), the active tab is derived from the current pathname.
   */
  activeItem?: string;
  /**
   * Optional override for tap behavior. When provided, the consumer is
   * responsible for navigation. When omitted, the nav uses
   * `react-router`'s `useNavigate` to push the item's `path`.
   */
  onItemClick?: (id: string) => void;
}

/**
 * Mobile bottom navigation bar, mounted once at the app shell. Hidden at
 * md+ where {@link SideNav} (the desktop sidebar) takes over.
 *
 * Lobby V2 design (Figma 5751-141388 "Menu"): a blurred translucent bar with
 * a hairline top border; the active tab is flat brand purple with a slightly
 * larger medium label — no background highlight or sliding indicator.
 * Extends its background behind the iOS home-indicator safe area while
 * keeping content above it.
 *
 * The active tab is derived from the current route via `useLocation`,
 * so consumers don't need to pass `activeItem`. Tapping a tab navigates
 * to its corresponding route via `react-router`. Pass `onItemClick` to
 * override (e.g. to track analytics or trigger a modal instead).
 *
 * @example
 * ```tsx
 * <BottomNav />
 * ```
 */
export function BottomNav({ activeItem, onItemClick }: BottomNavProps = {}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const resolvedActive = activeItem ?? resolveActiveItem(pathname);

  const handleClick = (item: NavItem) => {
    if (onItemClick) {
      onItemClick(item.id);
      return;
    }
    navigate(item.path);
  };

  return (
    <nav
      className={cn(
        // Full-width bottom bar. Height grows with the iOS safe-area inset so
        // the white background covers the home-indicator area; `items-end`
        // keeps buttons above it.
        'fixed bottom-0 left-0 right-0 w-full md:hidden',
        'pb-[env(safe-area-inset-bottom)]',
        // White-with-transparency + blur + hairline top border, per Figma.
        'bg-surface/80 backdrop-blur-[5px] border-t-[2.4px] border-solid border-stroke-soft',
        'z-50 flex items-end justify-center',
      )}
    >
      {bottomNavItems.map((item) => {
        const isActive = item.id === resolvedActive;
        const { Icon } = item;
        return (
          <button
            key={item.id}
            onClick={() => handleClick(item)}
            className={cn(
              'flex flex-col items-center justify-center gap-[5px] flex-1 h-[82px] pb-1.5',
              'bg-transparent border-none cursor-pointer relative',
            )}
          >
            <span
              className={cn(
                // Box is a stable 32px (Figma 32.4px). The icon grows on
                // activation — active fills the box (size-8 ≈ 32px), inactive
                // scales to 0.875 (≈ size-7, 28px) — animated via transform so
                // the surrounding layout doesn't shift. Inactive sits at 70%.
                'flex items-center justify-center size-8 origin-center',
                'transition duration-200 ease-out',
                isActive
                  ? cn('opacity-100 scale-100', item.activeClassName ?? 'text-purple')
                  : 'text-ink-muted opacity-70 scale-[0.875]',
              )}
            >
              <Icon className="size-full" active={isActive} />
            </span>
            <span
              className={cn(
                'font-sans',
                // Animate both color and font-weight. Outfit is loaded as a
                // variable font (see index.css) so 300 → 500 interpolates
                // smoothly instead of snapping.
                'transition-[color,font-weight] duration-200 ease-out',
                // Per Figma: active label 14px/medium/+0.28px purple, inactive 12px/light/+0.24px grey.
                isActive
                  ? cn('text-sm font-medium tracking-[0.28px]', item.activeClassName ?? 'text-purple')
                  : 'text-xs font-light tracking-[0.24px] text-ink-muted',
              )}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
